import { cache } from '@/lib/cache';

// Interface for terms sections
export interface TermsSection {
  id: string;
  title: string;
  content: string;
  order: number;
}

// Interface for terms data
export interface TermsData {
  title: string;
  sections: TermsSection[];
  totalCount: number;
  generatedAt: string;
}

// Cache for the terms data promise
let termsDataPromise: Promise<TermsData> | null = null;

/**
 * Dynamically import the terms data only when needed
 * This improves initial load time by deferring the loading of the data
 */
export async function loadTermsData(): Promise<TermsData> {
  if (termsDataPromise) {
    return termsDataPromise;
  }

  termsDataPromise = (async () => {
    try {
      const data = await import('@/data/terms.json');
      return data.default;
    } catch (error) {
      console.error('Error loading terms data:', error);
      return {
        title: 'Terms of Service',
        sections: [],
        totalCount: 0,
        generatedAt: new Date().toISOString()
      };
    }
  })();

  return termsDataPromise;
}

/**
 * Get initial terms data from static JSON
 * This provides fast initial loading without API calls
 */
export async function getInitialTermsData(): Promise<TermsData> {
  return loadTermsData();
}

/**
 * Get all sections for the terms page
 */
export async function getTermsSections(documentType: 'terms' | 'dpa' = 'terms'): Promise<TermsData> {
  if (documentType === 'dpa') {
    try {
      const data = await import('@/data/dpa.json');
      return data.default;
    } catch (error) {
      console.error('Error loading DPA data:', error);
      return {
        title: 'Data Processing Agreement',
        sections: [],
        totalCount: 0,
        generatedAt: new Date().toISOString()
      };
    }
  }
  return loadTermsData();
} 